import { Inject, Injectable, InjectionToken, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';

import { fromEvent, interval, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { UserService } from 'src/app/core/services/lmsuser.service';
import { SessionExpiredComponent } from 'src/app/shared/ui/session-expired/session-expired.component';
import { MdbModalRef, MdbModalService } from 'src/lib/mdb5-adapted/public-api';

export const LAST_ACTION_KEY = 'LMS_LastActionKEY';
export const CHECK_INTERVAL = 20000; // 20 seconds
export const AUTO_LOGOUT = new InjectionToken<number>('AUTO_LOGOUT');

@Injectable({ providedIn: 'root' })
export class AutoLogoutService implements OnDestroy {
  private logoutTimeoutId: any = null;
  private isExecutingLogout = false;
  private userActivitySub = new Subscription();
  private checkIntervalSub = new Subscription();
  private modalCloseSub: Subscription | null = null;
  private warned = false;
  private modalRef: MdbModalRef<SessionExpiredComponent> | null = null;
  private readonly visibilityChangeHandler = () => {
    if (!document.hidden) {
      this.check();
    }
  };

  constructor(
    private router: Router,
    private modalService: MdbModalService,
    private userService: UserService,
    private ngZone: NgZone,
    @Inject(AUTO_LOGOUT) private autoLogoutTime: number
  ) {
    this.setLastAction(Date.now());
    this.initUserActivityListeners();
    this.initCheckInterval();
    this.listenToVisibilityChange();
  }

  private getLastAction(): number {
    const lastAction = localStorage.getItem(LAST_ACTION_KEY);
    return lastAction ? parseInt(lastAction, 10) : Date.now();
  }

  private setLastAction(time: number): void {
    localStorage.setItem(LAST_ACTION_KEY, time.toString());
    this.check();
  }

  private initUserActivityListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      const activityEvents = ['click', 'keypress', 'scroll'];
      activityEvents.forEach((event) =>
        this.userActivitySub.add(
          fromEvent(document, event)
            .pipe(throttleTime(10000))
            .subscribe(() => this.setLastAction(Date.now()))
        )
      );
    });
  }

  private initCheckInterval(): void {
    this.ngZone.runOutsideAngular(() => {
      this.checkIntervalSub = interval(CHECK_INTERVAL).subscribe(() => this.check());
    });
  }

  private listenToVisibilityChange(): void {
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  public check(): void {
    const now = Date.now();
    const lastAction = this.getLastAction();
    const timeLeft = lastAction + this.autoLogoutTime - now;

    if (timeLeft <= 0) {
      void this.executeLogout();
    } else if (timeLeft <= 60000 && !this.warned) { // 1 minute warning
      this.warned = true;
      console.log('Auto-logout: Raised Warning!');
      this.showTimeoutWarning();
    } else if (timeLeft > 60000 && this.warned) {
      this.clearTimeouts();
    }
  }

  private async executeLogout(): Promise<void> {
    if (this.isExecutingLogout) {
      return;
    }

    this.isExecutingLogout = true;
    this.clearTimeouts();

    try {
      console.log('Auto-logout Logout');
      await this.userService.logOutUser();
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    } finally {
      this.isExecutingLogout = false;
    }
  }

  private showTimeoutWarning(): void {
    this.modalRef = this.modalService.open(SessionExpiredComponent, {
      modalClass: 'modal-sm modal-dialog-centered',
      ignoreBackdropClick: true,
      keyboard: false,
      data: {
        title: 'Session Timeout Warning',
        message: 'You will be logged out due to inactivity.'
      }
    });

    this.modalCloseSub?.unsubscribe();
    this.modalCloseSub = this.modalRef.onClose.subscribe((result: boolean) => {
      if (result === true) {
        // User clicked Continue - reset timer
        this.setLastAction(Date.now());
      } else {
        // Timeout completed or modal dismissed - force logout
        void this.executeLogout();
      }
      this.warned = false;
      this.modalRef = null;
    });
  }

  private clearTimeouts(): void {
    if (this.logoutTimeoutId) {
      clearTimeout(this.logoutTimeoutId);
      this.logoutTimeoutId = null;
    }
    this.modalCloseSub?.unsubscribe();
    this.modalCloseSub = null;

    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }

    this.warned = false;
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.userActivitySub.unsubscribe();
    this.checkIntervalSub.unsubscribe();
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);

  }
}
